import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSTooltip, useToast } from '@repo/design-system/index'
import {
  QIcon_bell_default,
  QIcon_bell_off,
  QIcon_bookmark,
  QIcon_calendar_filled,
  QIcon_computer,
  QIcon_question_outline,
  QIcon_thumbs_up,
} from '@repo/icons/index'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { tutorProfileQueryApi } from '../../../../modules/apis/student/tutors/queries'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { n_common, n_tutors, s_portal_home, s_tutors } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { saveTutor } from '../../../../modules/schedule/fetches'
import { isB2BHRGroup } from '../../../../modules/utils'
import { COLORS, TUTOR_SCHEDULE_OPEN_STATUS } from '../../../../modules/vars'
import { TUTOR_PROFILE_TAB } from './TutorBody'
import useTutorNotification from './useTutorNotification'

const DisabledScheduleButton = ({ scheduleStatus }) => {
  const { t } = useTranslation()
  return (
    <DSButton disabled={true} type={BUTTON_TYPE.OUTLINE} size={BUTTON_SIZE.MD} onClick={null} isIconMode={true}>
      <DSButton.Icon alt="calendar" className="mr-[4px]">
        <QIcon_calendar_filled color={COLORS.WHITE} />
      </DSButton.Icon>
      <DSButton.Label customTxtColor={COLORS.BLACK}>
        {scheduleStatus == TUTOR_SCHEDULE_OPEN_STATUS.FULL_BOOKED
          ? t(n_tutors.schedule_fully_booked)
          : t(s_tutors.not_schedule_yet)}
      </DSButton.Label>
    </DSButton>
  )
}

type TutorInfoCardPropsType = {
  data: any
  onClickHeart?: (e: React.MouseEvent<Element, MouseEvent>) => void
  tabId?: any
  setActiveId: any
  isFeaturedTutor?: boolean
  noReserve?: boolean
  isModal?: boolean
  refetch?: () => void
}

const TutorInfoCard = ({
  data,
  onClickHeart,
  tabId,
  setActiveId,
  isFeaturedTutor,
  noReserve,
  isModal,
  refetch,
}: TutorInfoCardPropsType) => {
  const { currentUser } = useCurrentUser()
  const router = useRouter()
  const { t, locale } = useTranslation()
  const { toastInfo } = useToast()
  const isB2BUser = isB2BHRGroup(currentUser)

  const isInSchedule = router.asPath.includes('/schedule')
  const [saved, setSaved] = useState()
  const [toggle, setToggle] = useState(false)

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: saveTutor,
    onSuccess: () => {
      setSaved(saved)
      queryClient.invalidateQueries(tutorProfileQueryApi.getQueryKey({ searchParams: { tutor_id: data.id, locale } }))
    },
    onError: (error) => {
      console.error(error)
    },
  })

  useEffect(() => {
    data && setSaved(data.my_tutor)
  }, [data])

  useEffect(() => {
    if (mutation.data && data && mutation.data.id == data.id) {
      setSaved(mutation.data.isAdd)
    }
  }, [mutation.data, data])

  const showToastMessage = (isSaved) => {
    toastInfo(isSaved ? t(s_portal_home.tutor_like_added) : t(s_portal_home.tutor_like_deleted))
  }

  const handleSave = async (e: React.MouseEvent, saved, showToast = true, onClickHeart = undefined) => {
    if (onClickHeart) {
      onClickHeart(e, data)
    } else if (!mutation.isLoading) {
      mutation.mutate({ tutorId: data.id, isAdd: saved, type: tabId })
    }

    setSaved(saved)
    if (showToast) {
      showToastMessage(saved)
    }
  }

  const handleSchedule = (e) => {
    setActiveId(TUTOR_PROFILE_TAB.SCHEDULE)
  }

  const { updateNotificationStatus } = useTutorNotification()

  if (!data) return null
  const { teach_junior } = data
  return (
    <div className="mt-[16px]">
      <div>
        <div className="flex items-center justify-between">
          {!noReserve && !isModal && (
            <div className="flex items-center">
              <img className="h-[56px] w-[56px] rounded-full object-cover" src={data.image_url} />
              <div className="Headline3-22Bd ml-[16px]">{data.name}</div>
            </div>
          )}
          {!isModal && (
            <div className="hidden sm:block">
              {currentUser && (
                <div>
                  {noReserve ? (
                    <></>
                  ) : (
                    <div className="flex justify-end whitespace-nowrap text-left gap-x-8">
                      {saved && (
                        <DSButton
                          type={BUTTON_TYPE.OUTLINE}
                          size={BUTTON_SIZE.MD}
                          onClick={() => updateNotificationStatus(data?.id, !data.tutor_notification, refetch)}
                          isIconMode={true}
                        >
                          <DSButton.Icon alt="bell">
                            {data.tutor_notification ? (
                              <QIcon_bell_default width={16} height={16} color={COLORS.GRAY700} />
                            ) : (
                              <QIcon_bell_off width={16} height={16} color={COLORS.GRAY300} />
                            )}
                          </DSButton.Icon>
                          <DSButton.Label className="ml-[4px]">스케줄 알림</DSButton.Label>
                        </DSButton>
                      )}
                      <div className="w-[105px]">
                        <DSButton
                          type={BUTTON_TYPE.OUTLINE}
                          size={BUTTON_SIZE.MD}
                          onClick={(e) => handleSave(e, !saved, true, onClickHeart)}
                          customBgColor={saved ? COLORS.RED50 : null}
                          isIconMode={true}
                        >
                          <DSButton.Icon alt="bookmark">
                            <QIcon_bookmark width={16} height={16} color={saved ? COLORS.RED400 : COLORS.GRAY300} />
                          </DSButton.Icon>
                          <DSButton.Label className="ml-[4px]">{t(n_tutors.mark_tutor)}</DSButton.Label>
                        </DSButton>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-[16px]">
          {/* INFO */}
          {data.diplomas[0]?.representative && (
            <>
              <div className="Body1-16Md">
                {data.diplomas[0].degree && <span>{data.diplomas[0].degree}, </span>}
                <span>{data.diplomas[0].major}</span>
              </div>
              <div className="Body2-14Md text-gray-600">{data.diplomas[0].institution_name}</div>
            </>
          )}
          {data.diplomas.length > 1 &&
            (toggle ? (
              data.diplomas.map((item, i) => (
                <div className="" key={item.id}>
                  {!item.representative && (
                    <div>
                      <div className="Body1-16Md">
                        {item.degree && <span>{item.degree},</span>}
                        <span>{item.major}</span>
                      </div>
                      <div className="Body2-14Md text-gray-600">{item.institution_name}</div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-14 mb-16 cursor-pointer text-purple-500 " onClick={() => setToggle(true)}>
                {t(n_common.btn_more)}
              </div>
            ))}
        </div>
        <div className="my-[16px] flex flex-row items-center">
          <div className={classNames('flex items-center')}>
            <div className="Body2-14Bd text-gray-500">
              <div>{t(n_tutors.accept_rate)}</div>
            </div>
            <span className={classNames('Body2-14Md ml-[8px] mr-[4px] text-gray-700')}>
              {data?.accept_rate == null ? '-' : data?.accept_rate}%
            </span>
            <DSTooltip
              isOpenOnClick
              place="bottom"
              isMultiline={true}
              triggerComponent={<QIcon_question_outline color={COLORS.GRAY500} />}
              contentComponent={data?.accept_rate == null ? t(n_tutors.no_history) : t(n_tutors.accept_rate_requested)}
            />
          </div>
          <div className="mx-[8px] h-[12px] w-[1px] bg-gray-200"></div>
          <div className="Body2-14Bd text-gray-700">
            <span>{t(n_tutors.with_tutor_count)}</span> <span className="ml-[8px]">{data?.lessons_count}</span>
          </div>
        </div>

        <div className="pb-16">
          {isInSchedule || noReserve ? (
            <></>
          ) : (
            <div className="w-full">
              {data.schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.AVAILABLE ? (
                data.applicable ? (
                  <DSButton
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.MD}
                    onClick={(e) => handleSchedule(e)}
                    customBorderColor={COLORS.PURPLE600}
                  >
                    <DSButton.Label className="ml-[4px]" customTxtColor={COLORS.PURPLE600}>
                      {t(n_tutors.btn_see_schedule)}
                    </DSButton.Label>
                  </DSButton>
                ) : (
                  <DisabledScheduleButton scheduleStatus={data.schedule_status} />
                )
              ) : (
                <DisabledScheduleButton scheduleStatus={data.schedule_status} />
              )}
            </div>
          )}
        </div>

        {(data.is_featured_tutor || isFeaturedTutor || data.is_rookie || (currentUser?.is_junior && !teach_junior)) && (
          <div className="mt-[16px] h-[1px] w-full bg-gray-200"></div>
        )}
        {(isFeaturedTutor || data.is_featured_tutor || data.is_rookie || (currentUser?.is_junior && !teach_junior)) && (
          <div className="flex flex-col gap-y-[16px] py-[20px]">
            {(isFeaturedTutor || data.is_featured_tutor) && (
              <div className="flex items-center">
                <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-gray-100">
                  <QIcon_thumbs_up color={COLORS.PURPLE500} />
                </div>
                <div className="ml-[16px]">
                  <div className="Body1-16Bd text-purple-500">{t(n_common.label_featured)}</div>
                  <div className="Body2-14Md text-gray-500">{t(n_tutors.label_featured_desc(data.name))}</div>
                </div>
              </div>
            )}
            {data.is_rookie && (
              <div className="flex items-center">
                <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-gray-100">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15.6537 2.99658C15.5125 2.99658 15.3714 2.99658 15.2303 3.00442C8.24451 3.23172 2.64648 8.96131 2.64648 15.9998C2.64648 23.0384 8.47188 29.0031 15.6537 29.0031C22.8354 29.0031 28.4256 23.4068 28.6452 16.4231C28.6452 16.282 28.653 16.1409 28.653 15.9998C28.653 8.82022 22.8276 2.99658 15.6458 2.99658H15.6537ZM15.6537 25.6327C10.33 25.6327 6.01784 21.3218 6.01784 15.9998C6.01784 11.3597 9.29512 7.4956 13.6622 6.57855C14.3051 6.44531 14.9715 6.36693 15.6537 6.36693C20.9773 6.36693 25.2895 10.6778 25.2895 15.9998C25.2895 16.6817 25.2189 17.348 25.0778 17.9907C24.1605 22.3565 20.2873 25.6327 15.6537 25.6327Z"
                      fill="#FEC3A8"
                    />
                    <path
                      d="M15.6494 5.73926C14.923 5.73926 14.2134 5.8144 13.5288 5.96468C8.87847 6.94149 5.38867 11.0658 5.38867 15.9999C5.38867 21.6688 9.98052 26.2606 15.6494 26.2606C20.5919 26.2606 24.7078 22.7708 25.6846 18.1205C25.8266 17.4359 25.91 16.7263 25.91 15.9999C25.91 10.3311 21.3182 5.73926 15.6494 5.73926Z"
                      fill="#F9725C"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.6543 22.2259C11.6543 22.7359 12.1254 23.1335 12.7298 23.1335C13.3264 23.1335 13.8227 22.7423 13.8227 22.2259V18.2865C14.4519 18.8495 15.3056 19.2333 16.4296 19.2333C17.7434 19.2333 18.7763 18.8549 19.4758 18.1578C20.1697 17.4663 20.5068 16.4901 20.5068 15.336V14.364C20.5068 13.289 20.1773 12.3157 19.4922 11.6064C18.801 10.8908 17.7693 10.4668 16.4296 10.4668C15.3368 10.4668 14.4634 10.795 13.8193 11.3156C13.775 10.8401 13.3048 10.4668 12.7298 10.4668C12.1178 10.4668 11.6543 10.8856 11.6543 11.3891V22.2259ZM16.1459 12.1335C16.8292 12.1335 17.3607 12.3326 17.725 12.6844C18.0932 13.0399 18.3165 13.5768 18.3165 14.2892V15.4085C18.3165 16.1229 18.1007 16.6601 17.7406 17.0151C17.3853 17.3655 16.8636 17.5642 16.1808 17.5642C15.5303 17.5642 14.9371 17.2995 14.5011 16.8895C14.0639 16.4785 13.8008 15.9356 13.8008 15.4085V14.0095C13.9674 12.9545 15.0469 12.1335 16.1459 12.1335Z"
                      fill="white"
                    />
                  </svg>
                </div>
                {!isB2BUser && (
                  <div className="ml-[16px]">
                    <div className="Body1-16Bd text-red-400">{t(n_common.label_pointback)}</div>
                    <div className="Body2-14Md text-gray-500">{t(n_tutors.label_pointback_desc(data.name))}</div>
                  </div>
                )}
              </div>
            )}
            {currentUser?.is_junior && !teach_junior && (
              <div className="flex items-center">
                <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-gray-100">
                  <QIcon_computer color={COLORS.BLUE500} />
                </div>
                <div className="ml-[16px]">
                  <div className="Body1-16Bd text-blue-500">{t(n_common.label_adults_only)}</div>
                  <div className="Body2-14Md text-gray-500">{t(n_tutors.label_adults_only_desc(data.name))}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
export default TutorInfoCard
